// Кнопка спина и скипа.
//
// Кнопка одна на обе роли: она меняет кадры и действие, но со сцены не уходит. Так Pixi
// не теряет её из виду и после подмены сразу показывает верный вид — наведённый, если
// курсор над ней, см. ui/button.ts.
//
// Лицо кнопки и есть обещание игроку: спин начинает новый раунд, скип торопит идущий.
// Другого действия за лицом нет, поэтому роль ставит только раунд спина и только явно,
// см. game/spinRound.ts.
//
// Кнопка стоит в углу макета, а не экрана: на широком экране поля по краям остаются пустыми,
// и кнопка не уезжает от барабанов. Её масштаб уменьшен, чтобы слева встала автоигра.
//
// Пробел жмёт ту же кнопку, что и мышь, со всеми её правилами: заблокированная не отвечает,
// а роль нажатия берётся текущая. Своего действия у клавиши нет.

import { Container } from 'pixi.js';

import type { UiFrame } from '../assets/catalog';
import type { TextureSheet } from '../assets/manager';

import { RoundButton, type ButtonFaces } from './button';
import { minTouchableScale, type GameLayout, viewportEdge } from './layout';
import { DESIGN_HEIGHT, DESIGN_WIDTH, EDGE_MARGIN } from './scene';

/** Большая исходная кнопка оставляет место autoplay слева от себя. */
export const SPIN_SCALE: number = 0.75;

/** Действие заблокированной кнопки: она нажатия не принимает, и звать ей нечего. */
const NOTHING = (): void => {};

/** Кнопка игрового поля: спин или скип. */
export class SpinControls {
  readonly view: Container = new Container();

  readonly #button: RoundButton;
  readonly #spinFaces: ButtonFaces;
  readonly #skipFaces: ButtonFaces;
  readonly #onSpin: () => void;

  /** Что делает нажатие сейчас. Меняется от роли кнопки. */
  #onPress: () => void;

  /**
   * @param onSpin Вызывается нажатием кнопки спина.
   */
  constructor(ui: TextureSheet<UiFrame>, onSpin: () => void) {
    this.#spinFaces = { idle: ui.spinIdle, hover: ui.spinHover, active: ui.spinActive };
    this.#skipFaces = { idle: ui.skipIdle, hover: ui.skipHover, active: ui.skipActive };
    this.#onSpin = onSpin;
    this.#onPress = onSpin;

    // Действие живёт снаружи кнопки: у скипа оно своё на каждый раунд.
    this.#button = new RoundButton(this.#spinFaces, () => this.#onPress());
    this.#button.view.scale.set(SPIN_SCALE);
    const radius: number = this.#button.radius * SPIN_SCALE;
    this.#button.view.position.set(DESIGN_WIDTH / 2 - EDGE_MARGIN - radius, DESIGN_HEIGHT / 2 - EDGE_MARGIN - radius);

    this.view.addChild(this.#button.view);
    window.addEventListener('keydown', this.#onKeyDown);
  }

  /** Снимает клавиатуру. Зовётся, когда сцена уходит с экрана. */
  destroy(): void {
    window.removeEventListener('keydown', this.#onKeyDown);
  }

  /** Двигает затухание кнопки. */
  update(seconds: number): void {
    this.#button.update(seconds);
  }

  /** На узком экране кнопка поднимается над полосой и становится удобной для касания. */
  layout(layout: GameLayout): void {
    const { bottom, bounds, composition, viewport } = layout;
    const mobile: boolean = composition === 'mobile';
    const scale: number = mobile ? minTouchableScale(viewport, this.#button.radius, 32, SPIN_SCALE) : SPIN_SCALE;
    const radius: number = this.#button.radius * scale;
    const edge: number = mobile ? viewportEdge(viewport) : EDGE_MARGIN;

    this.#button.view.scale.set(scale);
    this.#button.view.position.set(
      bounds.right - edge - radius,
      mobile ? bottom.buttonBottom - radius : bounds.bottom - EDGE_MARGIN - radius,
    );
  }

  /**
   * Ставит кнопку спина.
   *
   * @param afford Есть ли на что крутить. Ложь гасит кнопку: на счету меньше ставки,
   * и нажатие ушло бы к бэкенду только за отказом, см. ADR-22.
   */
  showSpin(afford: boolean = true): void {
    this.#onPress = this.#onSpin;
    this.#button.faces = this.#spinFaces;
    this.#button.enabled = afford;
  }

  /**
   * Ставит кнопку скипа. Что делает нажатие, задаёт раунд спина.
   *
   * @param onPress Действие нажатия. null блокирует кнопку: торопить сейчас нечего.
   */
  showSkip(onPress: (() => void) | null): void {
    this.#onPress = onPress ?? NOTHING;
    this.#button.faces = this.#skipFaces;
    this.#button.enabled = onPress !== null;
  }

  /**
   * Жмёт кнопку с клавиатуры.
   *
   * Долгое удержание пробела браузер повторяет, но кнопка нажимается один раз: спин
   * от зажатой клавиши не должен уходить очередью. Прокрутку страницы пробелом игра забирает
   * себе: в игре ей прокручивать нечего.
   */
  readonly #onKeyDown = (event: KeyboardEvent): void => {
    if (event.code !== 'Space' || event.repeat)
      return;

    event.preventDefault();
    this.#button.press();
  };
}
